/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  Space,
  Button,
  Popover,
  Tag,
  Row,
  Col,
  Skeleton,
  message,
  Tooltip,
  notification,
  Typography
} from 'antd';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { updateNavigationTourGuide, updatePlanModal } from 'store/user';
import { PlusOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import FileSaver from 'file-saver';
import SummaryCard from 'components/SummaryCard';
import EbzTable from 'components/EbzTable';
import EbzButton from 'components/EbzButton';
import EbzFilterGroup from 'components/EbzFilterGroup';
import Alert from 'components/Alert';
import { ExportIcon, NotitficationIcon } from 'utils/icons';
import referralsAPI from 'services/referrals';
import merchantAPI from 'services/merchants';
import currencyFormat from 'utils/currencyFormat';
import { activationStatus, kycStatus, merchantStatus } from 'constants';
import { KYC_STATUS, MERCHANT_STATUS, REFERRAL_TYPE } from 'constants/status';
import dayjs from 'dayjs';
import joyrideCustomTooltip from 'components/JoyrideComponent/JoyrideTooltip';
import joyrideWelcomeCustomTooltip from 'components/JoyrideComponent/JoyrideWelcomeCustomTooltip';
import trackingService from 'services/trackingService';
import EbzPopover from 'components/EbzPopover';
import EVENT_NAME from 'utils/const';
import { PRODUCT_APP_TYPE, TOUR_REFERAL_CONTENT } from './const';
import ReferralSignUpModal from './ReferralSignUpModal';

const filtersArr = [
  {
    label: 'Product',
    attr: 'products',
    data: PRODUCT_APP_TYPE
  },
  {
    label: 'KYC Status',
    attr: 'kyc_status',
    data: kycStatus
  },
  {
    label: 'Referral Status',
    attr: 'merchant_status',
    data: merchantStatus
  }
];

const searchArr = [
  {
    value: 'name',
    label: 'Name'
  },
  {
    value: 'merchant_id',
    label: 'Merchant ID'
  },
  {
    value: 'submerchant_id',
    label: 'Sub-Merchant ID'
  },
  {
    value: 'email',
    label: 'Email'
  },
  {
    value: 'mobile_number',
    label: 'Phone'
  }
];

function ReferralsListPage() {
  // Page State
  const [hasSummaryData, setHasSummaryData] = useState(false);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    nextCursor: null,
    prevCursor: null,
    pageNumber: null
  });
  const [filtersObj, setFiltersObj] = useState({});
  //
  const [api, contextHolder] = notification.useNotification();

  const isLeadAllowed = useSelector((state) =>
    state.user.planPermissions.can_view_leads ? state.user.planPermissions.can_view_leads : false
  );
  const canCreateLead = useSelector((state) =>
    state.user.planPermissions.can_create_lead ? state.user.planPermissions.can_create_lead : false
  );

  const isMerchantSignUpAllowed = useSelector(
    (state) => state.user.planPermissions.can_view_sign_up
  );
  const isSubmerchantSignUpAllowed = useSelector(
    (state) => state.user.planPermissions.can_create_submerchant
  );

  const [isReferralSignUpAllowed, setIsReferralSignUpAllowed] = useState(
    isMerchantSignUpAllowed || isSubmerchantSignUpAllowed
  );

  const planModal = useSelector((state) => state.user.planModal);
  const dispatch = useDispatch();
  const togglePlanModal = () => {
    dispatch(updatePlanModal(!planModal));
  };
  const permissions = useSelector((state) => state.user.planPermissions);

  const navigate = useNavigate();
  const configs = useSelector((state) => state.user.currentUser.configuration);
  const [tourGuideRun, setTourGuideRun] = useState(false);
  const [tourGuideSteps, setTourGuideSteps] = useState([]);
  const isDashboardAllowed = useSelector((state) =>
    state.user.planPermissions.can_view_dashboard
      ? state.user.planPermissions.can_view_dashboard
      : false
  );
  const isrefferalExportAllowed = useSelector((state) =>
    state.user.planPermissions.can_export_referral
      ? state.user.planPermissions.can_export_referral
      : false
  );
  const [tourStepIndex, setTourStepIndex] = useState(0);

  const tourGuide = useSelector((state) => state.user.navigationTourGuidetails);
  const [signUpModelDisplay, setSignUpModelDisplay] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [submitButtonDisable, setSubmitButtonDisable] = useState(false);

  // Apis function
  const fetchReferralsSummary = async (payload) => {
    const response = await referralsAPI.summary(payload);

    if (!response.success) {
      setHasSummaryData(false);
      console.log('something went wrong');
      // Alert('something went wrong', 'Please contact support team for this', 'error');
      return;
    }

    setSummary(response?.data ?? []);
  };

  const fetchReferralsList = async (type = 'next') => {
    setLoading(true);
    setSignUpModelDisplay(false);
    setSignupLoading(false);
    try {
      const newPagination =
        type === 'next'
          ? { next_cursor: pagination?.nextCursor, page_number: pagination?.pageNumber }
          : { prev_cursor: pagination?.prevCursor, page_number: pagination?.pageNumber };

      const response = await referralsAPI.list({
        ...newPagination,
        ...filtersObj
      });

      if (!response.success) {
        console.log('something went wrong');
        setHasData(false);
        return;
      }

      setPagination((prev) => ({
        ...prev,
        nextCursor:
          response?.data?.referrals?.length === response?.data?.page_size
            ? response?.data?.next_cursor
            : null,
        prevCursor: response?.data?.prev_cursor || null,
        pageNumber: response?.data?.page_number
      }));
      setLoading(false);
      setData(response?.data?.referrals ?? []);
      setHasData(true);
    } catch (error) {
      console.error('Error occurred while fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const tableStateUpdate = (type) => {
    setData([]);
    fetchReferralsList(type);
  };

  const merchantDownloadableReport = async () => {
    if (data?.length > 0) {
      const response = await merchantAPI.downloadableReport({
        current_page: pagination?.pageNumber,
        ...filtersObj
      });
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        Alert('something went wrong', 'Please contact support team for this', 'error');
      } else {
        api.open({
          message: 'CSV Export',
          description: 'Report has been generated.',
          icon: <NotitficationIcon />
        });
        FileSaver.saveAs(response?.data, `referrals_export.csv`);
      }
    } else {
      message.error({
        content: 'No records for CSV export'
      });
    }
  };
  //

  // Handle table pagination and filtering
  const handleTableChange = (filter) => {
    setFiltersObj(filter);
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: null
    }));
  };

  const applyFiltersCallbackfunc = (req) => {
    const [searchParams, checkboxGroup] = req;
    const filters = {
      ...searchParams,
      ...checkboxGroup
    };

    setFiltersObj(filters);
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: null
    }));
    setData([]);
  };

  const clearFiltersCallBackfun = () => {
    setFiltersObj({});
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: null
    }));
    setData([]);
  };

  const columnStyles = {
    fontWeight: 700,
    fontSize: '14px',
    textAlign: 'center',
    width: '100%',
    whiteSpace: 'nowrap'
  };

  const textStyle = {
    fontSize: '14px',
    width: '100%'
  };

  const columns = [
    {
      title: <Space style={columnStyles}>Name</Space>,
      dataIndex: 'name',
      key: 'name',
      ellipsis: {
        showTitle: false
      },
      width: 100,
      render: (name) => <Typography.Text style={textStyle}>{name}</Typography.Text>
    },
    {
      title: <Space style={columnStyles}>Referral ID</Space>,
      dataIndex: 'referred_id',
      key: 'referred_id',
      render: (referredId) => <Typography.Text style={textStyle}>{referredId}</Typography.Text>,
      width: 100
    },
    {
      title: <Space style={columnStyles}>Referral Type</Space>,
      dataIndex: 'referred_type',
      key: 'referred_type',
      width: 100,
      render: (referredType) => {
        return referredType ? (
          <Tag
            color={REFERRAL_TYPE[referredType].bgColor}
            style={{
              padding: '2px 13px',
              color: REFERRAL_TYPE[referredType].color,
              fontSize: '12px'
            }}
          >
            {REFERRAL_TYPE[referredType].label}
          </Tag>
        ) : (
          <>
            <Tag
              color="#EBEBEB"
              style={{ padding: '2px 13px', color: '#7B7B7B', fontSize: '12px' }}
            >
              ----
            </Tag>
            {/* {c} */}
          </>
        );
      }
    },
    {
      title: <Space style={columnStyles}>Phone Number</Space>,
      dataIndex: 'mobile_number',
      key: 'mobile_number',
      render: (mobileNumber) => <Typography.Text style={textStyle}>{mobileNumber}</Typography.Text>,
      width: 100
    },
    {
      title: <Space style={columnStyles}>Email</Space>,
      dataIndex: 'email',
      key: 'email',
      ellipsis: {
        showTitle: false
      },
      width: 100,
      render: (email) => <Typography.Text style={textStyle}>{email}</Typography.Text>
    },
    {
      title: <Space style={columnStyles}>Sign Up Date</Space>,
      dataIndex: 'merchant_created_time',
      key: 'merchant_created_time',
      align: 'center',
      width: 100,
      render: (merchantCreatedTime) => {
        return (
          <Typography.Text style={textStyle}>
            {merchantCreatedTime === null
              ? 'Invalid Date'
              : dayjs(merchantCreatedTime).format("DD MMM, 'YY")}
          </Typography.Text>
        );
      }
    },
    {
      title: <Space style={columnStyles}>State</Space>,
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => {
        return (
          <Typography.Text style={textStyle}>{state === null ? '---' : state}</Typography.Text>
        );
      }
    },
    {
      title: (
        <Space style={columnStyles}>
          Referral Status
          <Popover
            placement="bottomRight"
            content={
              <Row>
                <Col span={24}>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.LIVE_REFERRAL.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.ACTIVE_REFERRAL.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals who are live and transacting.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.ACTIVE_REFERRAL.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.LIVE_REFERRAL.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals for whom KYC has been approved but not started transacting.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.INACTIVE_REFERRAL.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.INACTIVE_REFERRAL.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals who have gone live but not transacted in the past 30 days.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: MERCHANT_STATUS.PENDING_REFERRAL.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {MERCHANT_STATUS.PENDING_REFERRAL.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            Referrals who have signed-up but not yet live.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            }
            trigger="hover"
            overlayClassName="ebz-filters-popover"
            overlayStyle={{
              width: '300px'
            }}
          >
            <InfoCircleOutlined style={{ color: '#A0A0A0' }} />
          </Popover>
        </Space>
      ),
      dataIndex: 'merchant_status',
      key: 'merchant_status',
      align: 'center',
      width: 100,
      render: (merchantStatusKey) => {
        return MERCHANT_STATUS[merchantStatusKey] ? (
          <Tag
            color={MERCHANT_STATUS[merchantStatusKey].bgColor}
            style={{
              padding: '2px 13px',
              color: MERCHANT_STATUS[merchantStatusKey].color,
              fontSize: '12px'
            }}
          >
            {MERCHANT_STATUS[merchantStatusKey].label}
          </Tag>
        ) : (
          <>
            <Tag
              color="#EBEBEB"
              style={{ padding: '2px 13px', color: '#7B7B7B', fontSize: '12px' }}
            >
              ----
            </Tag>
            {/* {c} */}
          </>
        );
      }
    },
    {
      title: (
        <Space style={columnStyles}>
          KYC Status
          <Popover
            placement="bottomRight"
            content={
              <Row>
                <Col span={24}>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.NOT_STARTED.bgColor
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.NOT_STARTED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            {KYC_STATUS.NOT_STARTED.description}
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.NOT_UPLOADED.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.NOT_UPLOADED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            {KYC_STATUS.NOT_UPLOADED.description}
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.PENDING_APPROVAL.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.PENDING_APPROVAL.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            {KYC_STATUS.PENDING_APPROVAL.description}
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.REJECTED.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col
                      span={22}
                      style={{
                        borderBottom: '1px solid #E8E8E8',
                        paddingBottom: '5px'
                      }}
                    >
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.REJECTED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            {KYC_STATUS.REJECTED.description}
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '4px' }}>
                    <Col span={2}>
                      <Space>
                        <div
                          style={{
                            height: '8px',
                            width: '8px',
                            borderRadius: '100%',
                            backgroundColor: KYC_STATUS.APPROVED.color
                          }}
                        />
                      </Space>
                    </Col>
                    <Col span={22}>
                      <Row>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '14px', fontWeight: '600' }}>
                            {KYC_STATUS.APPROVED.label}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text style={{ fontSize: '12px' }}>
                            {KYC_STATUS.APPROVED.description}
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            }
            trigger="hover"
            overlayClassName="ebz-filters-popover"
            overlayStyle={{
              width: '300px'
            }}
          >
            <InfoCircleOutlined style={{ color: '#A0A0A0' }} />
          </Popover>
        </Space>
      ),
      width: 100,
      dataIndex: 'kyc_status',
      key: 'kyc_status',
      align: 'center',
      render: (kycStatusKey, record) => {
        return KYC_STATUS[kycStatusKey] ? (
          <Tag
            color={KYC_STATUS[kycStatusKey].bgColor}
            style={{
              padding: '2px 13px',
              color: KYC_STATUS[kycStatusKey].color,
              fontSize: '12px'
            }}
          >
            {KYC_STATUS[kycStatusKey].label}
          </Tag>
        ) : (
          <>
            <Tag
              color="#EBEBEB"
              style={{ padding: '2px 13px', color: '#7B7B7B', fontSize: '12px' }}
            >
              ----
            </Tag>
            {/* {c} */}
          </>
        );
      }
    }
  ];

  const handleSignUpModal = () => {
    setSignUpModelDisplay(!signUpModelDisplay);
  };

  const fetchAndUpdateNavigation = async (paramKey) => {
    const request = {
      update: paramKey
    };
    const response = await merchantAPI.fetchNavigationFlag(request);
    if (response.success) {
      dispatch(updateNavigationTourGuide(response?.data));
    } else {
      console.log('something went wrong');
    }
  };

  const handleJoyrideCallback = (callbackProps) => {
    const { status, index, type, action, size } = callbackProps;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if ([ACTIONS.NEXT].includes(action)) {
        const nextIndex = index + 1;
        setTourStepIndex(nextIndex);
      } else {
        const prevIndex = index - 1;
        setTourStepIndex(prevIndex);
      }
      if (isDashboardAllowed && index === 1 && [ACTIONS.PREV].includes(action)) {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_refferal_guide');
      }
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setTourGuideRun(false);
    }
    const lastStepIndex = size - index;
    if ([ACTIONS.CLOSE].includes(action) || [EVENTS.TOUR_END].includes(type)) {
      if (lastStepIndex > 1) {
        setTourStepIndex(size - 1);
      } else {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_refferal_guide');
      }
    }
  };

  // effects
  useEffect(() => {
    if (permissions?.can_view_referrals) {
      setPagination({
        nextCursor: null,
        prevCursor: null,
        pageNumber: null
      });
      fetchReferralsSummary();
      fetchReferralsList();
    } else {
      navigate('/');
    }
  }, [filtersObj]);

  useEffect(() => {
    if (Object.keys(summary).length !== 0) {
      setHasSummaryData(true);
    }
  }, [summary]);
  //
  useEffect(() => {
    if (
      tourGuide?.dashboard_guide?.tour_guide &&
      tourGuide?.dashboard_guide?.dashboard_modules?.disable_refferal_guide
    ) {
      setTourGuideRun(!tourGuideRun);
      setTourGuideSteps(TOUR_REFERAL_CONTENT);
      setTourStepIndex(isDashboardAllowed ? 1 : 0);
    }
  }, [tourGuide, isDashboardAllowed]);

  return (
    <>
      <div
        style={{
          padding: '0 20px',
          margin: '20px 0px'
        }}
      >
        <Joyride
          callback={handleJoyrideCallback}
          continuous
          run={tourGuideRun}
          steps={tourGuideSteps}
          scrollOffset={64}
          showSkipButton
          styles={{
            options: {
              primaryColor: 'var(--color-fill-alter)',
              zIndex: 100
            }
          }}
          tooltipComponent={isDashboardAllowed ? joyrideCustomTooltip : joyrideWelcomeCustomTooltip}
          stepIndex={tourStepIndex}
        />
        {hasSummaryData === true ? (
          <SummaryCard summary={summary?.summary} updatedAt={summary?.updated_at} />
        ) : (
          <Row
            gutter={[8, 16]}
            style={{
              boxShadow: 'rgb(0 0 0 / 6%) 0px 3px 6px 4px',
              borderRadius: '10px',
              backgroundColor: 'white'
            }}
          >
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%',
                border: '4px solid',
                borderTop: '0',
                borderBottom: '0',
                borderLeft: '0',
                borderImage:
                  'linear-gradient(rgb(255, 255, 255) 10%, rgb(232, 232, 232) 48%, rgb(255, 255, 255) 90%) 1 / 1 / 0 stretch',
                borderImageSlice: '1'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%',
                border: '4px solid',
                borderTop: '0',
                borderBottom: '0',
                borderLeft: '0',
                borderImage:
                  'linear-gradient(rgb(255, 255, 255) 10%, rgb(232, 232, 232) 48%, rgb(255, 255, 255) 90%) 1 / 1 / 0 stretch',
                borderImageSlice: '1'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%',
                border: '4px solid',
                borderTop: '0',
                borderBottom: '0',
                borderLeft: '0',
                borderImage:
                  'linear-gradient(rgb(255, 255, 255) 10%, rgb(232, 232, 232) 48%, rgb(255, 255, 255) 90%) 1 / 1 / 0 stretch',
                borderImageSlice: '1'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
            <Col
              span={6}
              style={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                height: '100%',
                width: '100%'
              }}
            >
              <Skeleton
                active
                paragraph={{
                  rows: 2
                }}
              />
            </Col>
          </Row>
        )}
      </div>
      <div
        style={{
          padding: '10px 24px'
        }}
      >
        <Space
          direction="horizontal"
          style={{ width: '100%', paddingBottom: '10px', justifyContent: 'space-between' }}
        >
          <Space>
            <EbzPopover
              placement="bottomLeft"
              content={
                <EbzFilterGroup
                  searchParams={searchArr}
                  filters={filtersArr}
                  apply={applyFiltersCallbackfunc}
                  clear={clearFiltersCallBackfun}
                />
              }
              trigger="click"
              overlayStyle={{
                width: '400px'
              }}
              overlayClassName="ebz-filters-popover"
            >
              <EbzButton size="large" className="ebz-filters-btn">
                Search & Filter <DownOutlined />
              </EbzButton>
            </EbzPopover>
          </Space>
          <Space size={10}>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              size="large"
              style={{ boxShadow: '0px 3px 6px #0000000f' }}
              className="sign-up"
              onClick={() => {
                handleSignUpModal();
                trackingService.trackEvent(EVENT_NAME.BUTTON_CLICK, {
                  name: 'Sign Up'
                });
              }}
              disabled={!isReferralSignUpAllowed}
            >
              Sign Up
            </Button>

            {loading ? (
              <Button disabled icon={<ExportIcon />} size="large" className="export-btn">
                Export
              </Button>
            ) : (
              <EbzButton
                icon={<ExportIcon />}
                size="large"
                className="export-btn"
                onClick={merchantDownloadableReport}
                disabled={!isrefferalExportAllowed}
              >
                Export
              </EbzButton>
            )}
          </Space>
        </Space>
        <EbzTable
          columns={columns}
          rowKey={(r) => `${r.merchant_id}_referral`}
          dataSource={data.length > 0 ? data : []}
          onRow={(r) => ({
            onClick: () => {
              const queryParams = r.submerchant_id
                ? `?submerchant=${encodeURIComponent(r.submerchant_id)}&app=${encodeURIComponent(
                    r.referral_source || ''
                  )}`
                : '';
              navigate(`details/${r.merchant_id}${queryParams}`);
            }
          })}
          loading={loading}
          scroll={
            hasData ? { x: 1000, y: 500, scrollToFirstRowOnChange: true } : { x: 'max-content' }
          }
          thBgColor="#EEEDF1"
          onChange={handleTableChange}
          pagination={false}
          size="middle"
          fetchTransactions={tableStateUpdate}
          newPagination={pagination}
        />
        <ReferralSignUpModal
          signUpModelDisplay={signUpModelDisplay}
          toggleModal={handleSignUpModal}
          signupLoading={signupLoading}
          submitButtonDisable={submitButtonDisable}
          setSubmitButtonDisable={setSubmitButtonDisable}
          setSignupLoading={setSignupLoading}
          fetchReferralsList={clearFiltersCallBackfun}
          isMerchantSignUpAllowed={isMerchantSignUpAllowed}
          isSubmerchantSignUpAllowed={isSubmerchantSignUpAllowed}
        />
      </div>
      {contextHolder}
    </>
  );
}

export default ReferralsListPage;
